"use strict";
/**
 * @name PanelRowExpandable
 *
 * @description
 *  An expandable panel row is used to show/hide nested table data.  See the teamsnap patterns
 *  library for more information https://teamsnap-ui-patterns.netlify.com/patterns/components/panel.html
 *
 *  This component accepts an array of objects as `parentColumns` and `childColumns`.  These columns will spread the
 *  object out on the <PanelCell /> by default, unless a `renderColumn` function is passed.
 *
 * @example
 *  <PanelRowExpandable
 *    parentColumns={[{ children: 'Homer Simpson', mods: 'u-size1of2' }, { children: 'Marge Simpson', mods: 'u-size1of2' }]}
 *    childColumns={[{ children: 'Bart Simpson', mods: u-size1of2' }, { children: 'Lisa Simpson', mods: 'u-size1of2' }]}
 *    renderColumn={ column => <div style={{ outline: '1px dashed red' }} { ...column }></div> } />
 *
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    Object.defineProperty(o, k2, { enumerable: true, get: function() { return m[k]; } });
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
var React = __importStar(require("react"));
var PropTypes = __importStar(require("prop-types"));
var PanelRow_1 = require("../PanelRow");
var PanelCell_1 = require("../PanelCell");
var Icon_1 = require("../Icon");
var helpers_1 = require("../../utils/helpers");
var propTypes = {
    parentColumns: PropTypes.arrayOf(PropTypes.object).isRequired,
    childColumns: PropTypes.arrayOf(PropTypes.object),
    renderColumn: PropTypes.func,
    className: PropTypes.string,
    mods: PropTypes.string,
    style: PropTypes.object,
    testId: PropTypes.string,
    otherProps: PropTypes.object,
};
var PanelRowExpandable = function (_a) {
    var parentColumns = _a.parentColumns, childColumns = _a.childColumns, renderColumn = _a.renderColumn, className = _a.className, mods = _a.mods, style = _a.style, testId = _a.testId, otherProps = _a.otherProps;
    var _b = React.useState(false), isExpanded = _b[0], setIsExpanded = _b[1];
    var handleRowClick = function (e) {
        e.preventDefault();
        setIsExpanded(!isExpanded);
    };
    var renderChildLink = function (children) {
        var linkClasses = (0, helpers_1.getClassName)('Panel-expandableControl', isExpanded && 'is-expanded');
        return (React.createElement("button", { type: "button", onClick: handleRowClick, className: linkClasses, style: {
                backgroundColor: 'transparent',
                border: 'none',
            } },
            React.createElement("span", { className: "Panel-expandableControlIcon" },
                React.createElement(Icon_1.Icon, { name: "right" })),
            children));
    };
    var renderColumns = function (columns, includeLink) {
        if (includeLink === void 0) { includeLink = false; }
        return columns.map(function (column, index) {
            var columnChildren = column.children, props = __rest(column, ["children"]);
            var children = includeLink && index === 0 ? renderChildLink(columnChildren) : columnChildren;
            return renderColumn ? (renderColumn(__assign({ key: index, children: children }, props))) : (React.createElement(PanelCell_1.PanelCell, __assign({ key: index }, props), children));
        });
    };
    var renderChildColumns = function (childCols) {
        return (React.createElement("div", { className: (0, helpers_1.getClassName)('Panel-childRows', isExpanded && 'is-expanded') },
            React.createElement(PanelRow_1.PanelRow, { isWithCells: true }, renderColumns(childCols))));
    };
    var hasChildren = childColumns && childColumns.length > 0;
    return (React.createElement("div", __assign({ className: (0, helpers_1.getClassName)(className, mods), style: style, "data-testid": testId }, otherProps),
        React.createElement(PanelRow_1.PanelRow, { isWithCells: true, isParent: true }, renderColumns(parentColumns, hasChildren)),
        hasChildren && renderChildColumns(childColumns)));
};
PanelRowExpandable.defaultProps = {
    childColumns: null,
    renderColumn: null,
    className: 'Panel-expandableRow',
    mods: null,
    style: {},
    testId: null,
    otherProps: {},
};
exports.default = PanelRowExpandable;
